'use strict';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/pairwise';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/takeUntil';
import {Observable} from 'rxjs/Observable';
import {epicErrorHandler} from '../rx-utils';
import {onEnterView, onLeaveView} from './utils';


/**
 * @typedef {Object} ReduxItemListSupportEpicsOptions
 * @property {ReduxObservableListActions} actions
 * @property {Function} getState
 * @property {String} viewName The ui-router state name for the list view
 * @property {Array<String>} navStates router state name(s) that this list appears on. This should really be 1 value.
 * @property {bunyan} logger
 */

/**
 * ReduxList with parentId obtained from a ReduxItem
 * @param {ReduxItemListSupportEpicsOptions} options
 */
export function buildReduxItemListSupportEpics(options) {
  const {
    actions,
    getState,
    logger,
    navStates,
    reduxItem,
  } = options;

  return [
    onEnterList,
    onLeaveList,
  ];

  /**
   * Initialize the list in the view, and update it if the app state changes.
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable.<ReduxAction>}
   */
  function onEnterList(action$, store) {
    const getReduxItemState = reduxItem.options.getState;

    return onEnterView(action$, navStates)
      .switchMap(() => {
        const rootState = store.getState();
        const itemState = getReduxItemState(rootState);

        // watch the project for changes and re-init if needed
        const actionOut$ = action$.ofType(reduxItem.actions.initItem.TYPE)
          .map(action => action.payload.id)
          .startWith(itemState.id ? itemState.id : void 0)
          .pairwise()
          .map(([prevParentId, nextParentId]) => ({
            prevParentId,
            nextParentId,
          }))
          .switchMap(({prevParentId, nextParentId}) =>
            getListUpdateActions(prevParentId, nextParentId)
          )
          .takeUntil(onLeaveView(action$, navStates));

        // if the project is already loaded, init the list
        if (itemState.init) {
          return Observable
            .merge(
              Observable.of(actions.initList({parentId: itemState.id})),
              actionOut$,
            );

        } else {
          return actionOut$;
        }
      })
      .catch(epicErrorHandler(logger, 'onEnterList'));
  }

  /**
   * Destroy the list after leaving the view.
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @return {Observable.<ReduxAction>}
   */
  function onLeaveList(action$, store) {
    return onLeaveView(action$, navStates)
      .switchMap(() => {
        const rootState = store.getState();
        const listState = getState(rootState);

        if (listState.init) {
          return Observable.of(actions.destroyList(void 0, {parentId: listState.parentId}));
        } else {
          return Observable.empty();
        }
      })
      .catch(epicErrorHandler(logger, 'onLeaveList'));
  }

  function getListUpdateActions(prevParentId, nextParentId) {
    if (prevParentId && nextParentId) {
      return Observable.of(
        actions.destroyList(void 0, {parentId: prevParentId}),
        actions.initList({parentId: nextParentId}),
      );
    } else if (prevParentId) {
      return Observable.of(actions.destroyList(void 0, {parentId: prevParentId}));
    } else if (nextParentId) {
      return Observable.of(actions.initList({parentId: nextParentId}));
    } else {
      return Observable.empty();
    }
  }
}



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux/listSupportEpics.js